import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table"
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import type { SubscriptionOptions } from "@reduxjs/toolkit/query"
import { useLocation, useMatch, useNavigate } from "react-router-dom"

import "@tanstack/react-table"
import { RowData } from "@tanstack/table-core"
import { VisibilityState } from "@tanstack/table-core/src/features/ColumnVisibility.ts"

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    mobileVisible?: boolean
  }
}

export function useTable({ defaultPageSize = 10 }: { defaultPageSize?: number }) {
  const location = useLocation()
  const [pagination, setPagination] = React.useState<PaginationState>(
    location.state?.table?.pagination || {
      pageIndex: 0,
      pageSize: defaultPageSize,
    },
  )

  const resetPagination = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [])

  return { pagination, resetPagination, setPagination }
}

export type Column<T> = ColumnDef<T> & {
  mobileVisible?: boolean
}
type VisibilityOptions = {
  disablePagination?: boolean
}
const mobileBreakpoint = 768
export function ReactTailwindTable<T = any>({
  query,
  queryParams,
  queryOptions,
  columns,
  defaultSorting = [],
  pagination,
  setPagination,
  visibilityOptions = {},
  columnVisibility = {},
  expandedRows,
  renderRowSubComponent,
  tableId = "table",
  rowId,
  downloadable,
  downloadLimit = 1000,
  downloadFilename = "data",
}: {
  query: any
  columns: Column<T>[]
  queryParams?: any
  queryOptions?: SubscriptionOptions
  defaultPageSize?: number
  defaultSorting?: SortingState
  pagination: PaginationState
  setPagination: OnChangeFn<PaginationState>
  originPath?: string
  visibilityOptions?: VisibilityOptions
  columnVisibility?: VisibilityState
  renderRowSubComponent?: (props: { row: any }) => React.ReactNode
  expandedRows?: string[]
  tableId?: string
  rowId?: string | Extract<keyof T, string> | ((row: T, index: number) => string)
  downloadable?: boolean
  downloadLimit?: number
  downloadFilename?: string
}) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const navigate = useNavigate()
  const location = useLocation()
  const [originPath] = useState(location.pathname)
  const isPathMatch = useMatch(originPath)
  const [downloadRequested, setDownloadRequested] = useState(false)

  const { pageSize, pageIndex } = pagination

  const [sorting, setSorting] = React.useState<SortingState>(location.state?.table?.sorting || defaultSorting || [])
  const fetchDataOptions = {
    skip: pageIndex * pageSize,
    take: pageSize,
    ...queryParams,
  }
  if (sorting.length) {
    fetchDataOptions.orderBy = sorting.map(s => (s.desc ? "-" : "+") + s.id)?.[0]
  }
  const { data, isLoading, isFetching } = query(fetchDataOptions, queryOptions)

  const downloadFetchOptions = {
    skip: 0,
    take: downloadLimit,
    ...queryParams,
    ...(sorting.length ? { orderBy: sorting.map(s => (s.desc ? "-" : "+") + s.id)?.[0] } : {}),
  }
  const { data: downloadData, isFetching: isDownloadFetching } = query(downloadFetchOptions, {
    ...queryOptions,
    skip: !downloadRequested,
  })

  useEffect(() => {
    if (!downloadRequested || isDownloadFetching || !downloadData?.results) return
    const blob = new Blob([JSON.stringify(downloadData.results, null, 2)], { type: "application/json" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${downloadFilename}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setDownloadRequested(false)
  }, [downloadRequested, isDownloadFetching, downloadData, downloadFilename])
  const defaultData = React.useMemo(() => [], [])

  const dynamicColumns = useMemo(() => {
    if (windowWidth < mobileBreakpoint) {
      return columns.filter(column => column.meta?.mobileVisible)
    }
    return columns
  }, [windowWidth, columns])
  const table = useReactTable<T>({
    data: data?.results ?? defaultData,
    columns: dynamicColumns,
    pageCount: data?.count ? Math.ceil(data?.count / pageSize) : -1,
    state: {
      pagination,
      sorting,
      columnVisibility,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getRowId: rowId
      ? (originalRow, index) => {
          if (typeof rowId === "function") {
            return rowId(originalRow, index)
          }
          const value = (originalRow as Record<string, unknown>)?.[rowId]
          return value != null ? String(value) : String(index)
        }
      : undefined,
    manualPagination: true,
    debugTable: true,
  })
  useEffect(() => {
    if (!isPathMatch) {
      return
    }
    const table = location.state?.[tableId] || {}
    if (
      pagination?.pageSize !== table?.pagination?.pageSize ||
      pagination?.pageIndex !== table?.pagination?.pageIndex ||
      sorting.length !== table?.sorting?.length ||
      sorting?.[0]?.id !== table?.sorting?.[0]?.id ||
      sorting?.[0]?.desc !== table?.sorting?.[0]?.desc
    ) {
      console.log(table)
      console.log("table navigate", {
        ...location.state,
        [tableId]: {
          pagination,
          sorting,
        },
      })
      navigate("." + location.search, {
        replace: true,
        state: {
          ...location.state,
          [tableId]: {
            pagination,
            sorting,
          },
        },
      })
    }
  }, [
    isPathMatch,
    location.search,
    location.state,
    navigate,
    pagination,
    pagination?.pageIndex,
    pagination?.pageSize,
    sorting,
    tableId,
  ])

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth)
      window.addEventListener("resize", handleResize)
    }
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm sm:text-base table-zebra">
        <thead className="bg-teal-100 dark:bg-teal-950 text-teal-800 dark:text-teal-700">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={
                      columns.find(col => col.accessorKey === header.column.id)?.size
                        ? { width: header.getSize() }
                        : undefined
                    }
                    className={"py-2 px-3 sm:px-4 text-left font-medium  "}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: `${"\u00A0"}▲`,
                          desc: `${"\u00A0"}▼`,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          <tr>
            <td colSpan={columns.length} className={"text-center p-0"}>
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isLoading || isFetching ? "max-h-20" : "max-h-0"
                }`}
              >
                <span className="loading loading-spinner loading-lg m-2"></span>
              </div>
            </td>
          </tr>
          {table.getRowModel().rows.map(row => {
            return (
              <React.Fragment key={`table-${tableId}-${row.id}`}>
                <tr className="odd:bg-base even:bg-accent hover">
                  {row.getVisibleCells().map(cell => {
                    return (
                      <td key={cell.id} className="px-3 sm:px-4 py-2">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    )
                  })}
                </tr>
                {expandedRows?.includes(row.id) && renderRowSubComponent && renderRowSubComponent({ row })}
              </React.Fragment>
            )
          })}
          {!table.getRowModel().rows.length && !isLoading && (
            <tr>
              <td colSpan={columns.length} className={"text-center"}>
                Пока нет данных
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="h-2" />
      <div className="flex items-center gap-4 flex-wrap">
        {!visibilityOptions.disablePagination && (
          <div className="join">
            <button
              className="join-item btn btn-ghost"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              «
            </button>
            <button
              className="join-item btn btn-ghost"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Предыдущая
            </button>
            <button className="join-item btn">
              {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </button>
            <button
              className="join-item btn btn-ghost"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Следующая
            </button>
            <button
              className="join-item btn btn-ghost"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              »
            </button>
          </div>
        )}
        {downloadable && (
          <button
            className="btn btn-sm btn-outline"
            onClick={() => setDownloadRequested(true)}
            disabled={isDownloadFetching && downloadRequested}
          >
            {isDownloadFetching && downloadRequested ? <span className="loading loading-spinner loading-xs" /> : "⬇"}
            Скачать JSON
          </button>
        )}
      </div>
      <div className="hidden">{table.getRowModel().rows.length} Rows</div>
      <pre className="hidden">{JSON.stringify(table.getState().pagination, null, 2)}</pre>
    </div>
  )
}

export const ActionsContainer = ({ children }: React.PropsWithChildren) => {
  return <div className={"gap-4  flex flex-col items-start"}>{children}</div>
}
